/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

// Cart custom elements that advertise sections via getSectionsToRender().
// If Dawn adds a new cart custom element, add its tag here.
const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

// Sections that Dawn's own pubsub subscribers refresh (cart.js's
// CartItems#onCartUpdate fetches and replaces these directly when
// cart-update fires; cart-drawer.js's renderContents handles the
// drawer body). We skip them here to avoid double-rendering.
// Format is '<element-tag>:<getSectionsToRender entry id>'.
// If you change which sections those subscribers refresh, update this set.
const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

// Walk every mounted Dawn cart custom element, collect the sections it
// wants rendered, and dedupe. Returns a Map keyed by section id, each
// entry pointing at the DOM mount and the selector used to extract the
// fresh fragment.
function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      // Two patterns coexist in getSectionsToRender():
      //   - cart-items style: entry.section is the parent Liquid section
      //     id, entry.selector is a child node inside it.
      //   - cart-drawer / cart-notification style: entry.id IS the mount;
      //     there is no parent wrapper.
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

// After a Storefront API mutation, refresh every Dawn cart section
// that isn't already refreshed by Dawn's own pubsub subscribers, then
// publish 'cart-update' so the subscribers run.
//
// We always fetch /cart.js (with sections= when we have any) so that
// `cartData` is defined for subscribers. quick-add-bulk.js reads
// `event.cartData.items` unconditionally — publishing without cartData
// makes it throw.
async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size
    ? `?sections=${[...sections.keys()].join(',')}`
    : '';
  // `routes` is a Dawn global, but don't assume it's defined.
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }

  // Hand off to Dawn's existing subscribers. cartData is the full
  // Cart Ajax payload (items, item_count, token, …) plus sections;
  // quick-add-bulk.js and price-per-item.js read it directly.
  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ?? undefined,
  });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    // Dawn doesn't currently listen for shopify:cart:* events, but the
    // bundle requires an eventTarget. document is the conventional root.
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}

/**
 * Novaria — traducción de metadatos dinámicos de filtros de Shopify.
 *
 * Search & Discovery puede entregar filter.label/value.label en inglés aunque
 * el locale español de Dawn esté correctamente traducido. Esta capa actúa
 * únicamente dentro de las interfaces de filtros/ordenamiento y vuelve a
 * aplicarse después de los refrescos AJAX de colecciones y búsqueda.
 */
(() => {
  const FACET_SCOPE = [
    '.facets-container',
    'facet-filters-form',
    '.mobile-facets',
    '#FacetFiltersForm',
    '#FacetFiltersFormMobile',
    '#FacetFiltersPillsForm',
  ].join(',');

  const translations = new Map([
    ['Availability', 'Disponibilidad'],
    ['Price', 'Precio'],
    ['Product type', 'Tipo de producto'],
    ['Vendor', 'Marca'],
    ['In stock', 'Disponible'],
    ['Out of stock', 'Agotado'],
    ['Filter and sort', 'Filtrar y ordenar'],
    ['Filter:', 'Filtro:'],
    ['Sort by', 'Ordenar por'],
    ['Sort by:', 'Ordenar por:'],
    ['Featured', 'Características'],
    ['Best selling', 'Más vendidos'],
    ['Alphabetically, A-Z', 'Alfabéticamente, A-Z'],
    ['Alphabetically, Z-A', 'Alfabéticamente, Z-A'],
    ['Price, low to high', 'Precio: menor a mayor'],
    ['Price, high to low', 'Precio: mayor a menor'],
    ['Date, old to new', 'Fecha: antigua a reciente'],
    ['Date, new to old', 'Fecha: reciente a antigua'],
    ['Relevance', 'Relevancia'],
    ['Clear', 'Quitar'],
    ['Clear all', 'Quitar todos'],
    ['Apply', 'Aplicar'],
    ['Remove', 'Quitar'],
    ['From', 'Desde'],
    ['To', 'Hasta'],
  ]);

  const translateValue = (value) => {
    if (typeof value !== 'string' || !value.trim()) return value;

    const leading = value.match(/^\s*/)?.[0] || '';
    const trailing = value.match(/\s*$/)?.[0] || '';
    const core = value.trim();

    if (translations.has(core)) {
      return `${leading}${translations.get(core)}${trailing}`;
    }

    let translated = core;
    translated = translated.replace(/^Availability\b/, 'Disponibilidad');
    translated = translated.replace(/^Price\b/, 'Precio');
    translated = translated.replace(/^Product type\b/, 'Tipo de producto');
    translated = translated.replace(/^Vendor\b/, 'Marca');
    translated = translated.replace(/\bIn stock\b/g, 'Disponible');
    translated = translated.replace(/\bOut of stock\b/g, 'Agotado');

    return translated === core ? value : `${leading}${translated}${trailing}`;
  };

  const translateAttributes = (element) => {
    if (!(element instanceof Element)) return;
    ['aria-label', 'title', 'placeholder'].forEach((attribute) => {
      if (!element.hasAttribute(attribute)) return;
      const current = element.getAttribute(attribute);
      const translated = translateValue(current);
      if (translated !== current) element.setAttribute(attribute, translated);
    });
  };

  const translateTree = (root) => {
    if (!root) return;

    if (root.nodeType === Node.TEXT_NODE) {
      const translated = translateValue(root.nodeValue);
      if (translated !== root.nodeValue) root.nodeValue = translated;
      return;
    }

    if (!(root instanceof Element)) return;

    translateAttributes(root);
    root.querySelectorAll('[aria-label], [title], [placeholder]').forEach(translateAttributes);

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      const translated = translateValue(node.nodeValue);
      if (translated !== node.nodeValue) node.nodeValue = translated;
    }
  };

  const processMutationNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      if (node.parentElement?.closest(FACET_SCOPE)) translateTree(node);
      return;
    }

    if (!(node instanceof Element)) return;

    if (node.matches(FACET_SCOPE) || node.closest(FACET_SCOPE)) {
      translateTree(node);
    }

    node.querySelectorAll(FACET_SCOPE).forEach(translateTree);
  };

  const initSpanishFacets = () => {
    document.querySelectorAll(FACET_SCOPE).forEach(translateTree);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach(processMutationNode);
      });
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSpanishFacets, { once: true });
  } else {
    initSpanishFacets();
  }
})();

/**
 * Novaria — protección disuasoria de contenido.
 *
 * Aumenta la fricción para copiar contenido desde la interfaz normal del
 * navegador sin interferir con compra, formularios, swipe, botones o el
 * editor de temas. No pretende ser DRM: cualquier recurso entregado al
 * navegador puede ser recuperado por un usuario técnicamente avanzado.
 */
(() => {
  const html = document.documentElement;
  const isDesignMode = () => Boolean(window.Shopify?.designMode || html.classList.contains('shopify-design-mode'));

  if (isDesignMode()) return;

  const allowedSelector = [
    'input',
    'textarea',
    'select',
    '[contenteditable="true"]',
    '[contenteditable="plaintext-only"]',
    '[data-novaria-copy-allowed]',
  ].join(',');

  const isAllowedTarget = (target) =>
    target instanceof Element && Boolean(target.closest(allowedSelector));

  const protectionStyle = document.createElement('style');
  protectionStyle.id = 'novaria-content-protection';
  protectionStyle.textContent = `
    html:not(.shopify-design-mode) body,
    html:not(.shopify-design-mode) body * {
      -webkit-touch-callout: none;
    }

    html:not(.shopify-design-mode) body,
    html:not(.shopify-design-mode) body *:not(input):not(textarea):not(select):not([contenteditable="true"]):not([contenteditable="plaintext-only"]):not([data-novaria-copy-allowed]) {
      -webkit-user-select: none;
      user-select: none;
    }

    html:not(.shopify-design-mode) input,
    html:not(.shopify-design-mode) textarea,
    html:not(.shopify-design-mode) select,
    html:not(.shopify-design-mode) [contenteditable="true"],
    html:not(.shopify-design-mode) [contenteditable="true"] *,
    html:not(.shopify-design-mode) [contenteditable="plaintext-only"],
    html:not(.shopify-design-mode) [contenteditable="plaintext-only"] *,
    html:not(.shopify-design-mode) [data-novaria-copy-allowed],
    html:not(.shopify-design-mode) [data-novaria-copy-allowed] * {
      -webkit-user-select: text !important;
      user-select: text !important;
      -webkit-touch-callout: default;
    }

    html:not(.shopify-design-mode) img,
    html:not(.shopify-design-mode) picture,
    html:not(.shopify-design-mode) video,
    html:not(.shopify-design-mode) svg,
    html:not(.shopify-design-mode) canvas,
    html:not(.shopify-design-mode) model-viewer {
      -webkit-user-drag: none;
      user-drag: none;
    }

    html:not(.shopify-design-mode) img,
    html:not(.shopify-design-mode) video {
      -webkit-touch-callout: none;
    }
  `;
  document.head.appendChild(protectionStyle);

  const preventHard = (event) => {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();
  };

  document.addEventListener(
    'contextmenu',
    (event) => {
      if (isDesignMode() || isAllowedTarget(event.target)) return;
      preventHard(event);
    },
    true
  );

  document.addEventListener(
    'selectstart',
    (event) => {
      if (isDesignMode() || isAllowedTarget(event.target)) return;
      preventHard(event);
    },
    true
  );

  document.addEventListener(
    'dragstart',
    (event) => {
      if (isDesignMode() || !(event.target instanceof Element)) return;
      if (event.target.closest('img, picture, video, svg, canvas, model-viewer')) {
        preventHard(event);
      }
    },
    true
  );

  document.addEventListener(
    'keydown',
    (event) => {
      if (isDesignMode()) return;

      const key = String(event.key || '').toLowerCase();
      const code = String(event.code || '').toLowerCase();
      const ctrlOrMeta = event.ctrlKey || event.metaKey;

      const f12 = key === 'f12' || code === 'f12';
      const windowsDevTools = event.ctrlKey && event.shiftKey && ['i', 'j', 'c', 'k'].includes(key);
      const macDevTools = event.metaKey && event.altKey && ['i', 'j', 'c', 'k', 'u'].includes(key);
      const viewSourceOrSave = ctrlOrMeta && ['u', 's'].includes(key);
      const contentCopyShortcut =
        ctrlOrMeta && !isAllowedTarget(event.target) && ['a', 'c', 'x'].includes(key);

      if (f12 || windowsDevTools || macDevTools || viewSourceOrSave || contentCopyShortcut) {
        preventHard(event);
      }
    },
    true
  );

  const hardenImage = (image) => {
    if (!(image instanceof HTMLImageElement)) return;
    image.draggable = false;
    image.setAttribute('draggable', 'false');
    image.dataset.novariaProtectedMedia = 'true';
  };

  const hardenTree = (root) => {
    if (!(root instanceof Element || root instanceof Document)) return;
    if (root instanceof HTMLImageElement) hardenImage(root);
    root.querySelectorAll?.('img').forEach(hardenImage);
  };

  hardenTree(document);

  const observer = new MutationObserver((mutations) => {
    if (isDesignMode()) {
      observer.disconnect();
      return;
    }

    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) hardenTree(node);
      });
    });
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();
